<script lang="ts">
    import { getBorderRadius } from '$lib/global/helper/border-radius';
    import { cn } from '$lib/utils/cn';

    import { afterUpdate } from 'svelte';

    import StatusIcon from './StatusIcon.svelte';
    import Emoji from '../../_wrappers/Emoji.svelte';

    import type { FormInputBinding } from '$lib/types/Components/SharedTypes';
    import type { HTMLInputTypeAttribute } from 'svelte/elements';

    export let placeholder = '';
    export let showEmoji = false;
    export let emoji = '';
    export let required = false;
    export let invalid = false;
    export let pristine = true;
    export let touched = false;
    export let active = false;
    export let type = 'text';
    export let finalFormInput: FormInputBinding = {
        name: '',
        value: '',
        onChange: () => {},
        onBlur: () => {},
        onFocus: () => {},
    };

    export let errorMessage = '';
    export let focusColor = '';
    export let outlineColor = '';

    let showError = invalid && touched;
    let showSuccess = !invalid && touched && !pristine;
    let showCheckmark = ['email', 'phone', 'url'].includes(type);
    let inputContainerClasses: string;
    let inputClasses: string;

    const getHtmlInputType = (type: string): HTMLInputTypeAttribute => {
        switch (type) {
            case 'phone':
                return 'tel';
            case 'url':
                return 'text';
            default:
                return type;
        }
    };

    afterUpdate(() => {
        showError = invalid && touched;
        showSuccess = !invalid && touched && !pristine;
        showCheckmark = ['email', 'phone', 'url'].includes(type);
    });

    const handleInput = (evt: Event) => {
        const target = evt.target as HTMLInputElement;

        let value = target.value;

        // Trim email, phone and URL values right away to avoid failing validation due to leading/trailing spaces
        if (showCheckmark) {
            value = value.trim();
        }

        finalFormInput.onChange(value);
    };

    const borderRadius = getBorderRadius('fieldsWrapper');

    $: inputContainerClasses = cn(
        'fun-box-border fun-w-full fun-border fun-bg-white fun-pl-4 fun-align-middle fun-ring-2 fun-ring-transparent fun-transition-all',
        borderRadius,
        {
            ['fun-pl-12']: showEmoji,
            ['fun-h-20']: invalid && showError,
            ['fun-h-14']: !invalid || !showError,
            ['fun-border-[var(--focusColor)] fun-ring-[var(--outlineColor)]']: active,
        },
    );

    $: inputClasses = cn(
        'fun-w-full fun-appearance-none fun-rounded-none fun-border-none fun-bg-transparent fun-align-middle fun-font-ui fun-text-base fun-font-light fun-placeholder-gray-500 fun-outline-none fun-transition',
        {
            'fun-h-full': !invalid || !showError,
            'fun-h-14': invalid && showError,
            'fun-pr-24': !required && pristine,
            'fun-pr-12': invalid && showError,
            'fun-pr-10': (required || !pristine) && (!invalid || !showError),
        },
    );
</script>

<div class="fun-relative fun-box-border fun-flex fun-items-center">
    {#if showEmoji}
        <div
            class="fun-absolute fun-left-4 fun-top-0 fun-flex fun-h-14 fun-select-none fun-items-center"
        >
            <u class="fun-no-underline" aria-hidden="true">
                <Emoji {emoji} size="20px" />
            </u>
        </div>
    {/if}

    {#if !required && !invalid && pristine}
        <u
            class="fun-absolute fun-right-6 fun-text-xs fun-font-light fun-text-gray-500 fun-no-underline"
            >(optional)</u
        >
    {/if}

    <StatusIcon {showCheckmark} {showError} {showSuccess} />

    <div
        class={inputContainerClasses}
        style="--outlineColor: {outlineColor}; --focusColor: {focusColor}"
    >
        <input
            class={inputClasses}
            type={getHtmlInputType(type)}
            pattern={type === 'number' ? '[0-9]*' : undefined}
            {placeholder}
            name={finalFormInput.name}
            value={finalFormInput.value || ''}
            on:blur={finalFormInput.onBlur}
            on:focus={finalFormInput.onFocus}
            on:input={handleInput}
            aria-label={placeholder}
        />

        {#if errorMessage && showError}
            <div class="fun--mt-2.5 fun-text-xs fun-font-normal fun-leading-tight fun-text-red-600">
                {errorMessage}
            </div>
        {/if}
    </div>
</div>
